/**
 * **Meta 광고 질문의 mockup 갈래** — ES 쪽 `actions/els/els-mock` 과 **대칭**이다.
 *
 * ```
 *   actions/els/ads-meta.ts        ads.list · ads.facets · ads.detail  머리에서 resolveMock*() 한 줄
 *   actions/meta-ads/…             유사 소재 · 키워드 실시간 수집        같은 줄
 *     │  잡히면 픽스처, 아니면
 *     ▼
 *   /api/els/ads-meta · /api/meta-ads/**   평소 그대로
 * ```
 *
 * 광고 소재 조사 화면은 질문이 **두 창구로 갈라져 있다** — ES 프록시(op 세 개)와 BFF
 * (`/api/meta-ads/**`) 다. 화면 하나의 mockup 이 두 레지스트리를 켜야 하면 켜고 끄는 자리가
 * 둘이 되므로, 이 화면의 어휘는 창구와 무관하게 여기 한 곳에 모은다.
 *
 * ## ⚠️ 판정이 질문마다 다르다
 * ```
 *   ads.list · ads.facets · ads.stats   「등록됐는가」 — 파라미터에 id 칸이 없다(필터·페이지뿐)
 *   ads.brandFaces · ads.byPublisher    「등록됐는가」 — 브랜드·계정 이름뿐이다
 *   ads.detail · similar(adId)          hasMockupId — `adId` 가 MOCKUP_ 접두인 것만
 *   ads.thumbs                          hasMockupId — `adIds` 가 전부 MOCKUP_ 접두일 때만(핸들러 몫)
 *   keyword-collect                     「등록됐는가」 — keyword·country 뿐이다
 * ```
 * 등록은 mockup 라우트의 layout 이 마운트돼 있는 동안만이고 언마운트에 풀리므로, 「등록됐는가」로만
 * 가르는 질문도 실화면 호출에는 닿지 않는다.
 *
 * ## ⚠️ 타입을 좁혀 둔 이유
 * 핸들러마다 Request/Response 를 경계 타입(`types/ads-meta/*` · 이 폴더의 유선 타입)으로 못박았다.
 * 계약이 바뀌면 **픽스처가 tsc 에서 운다** — 단언(`as`)으로 헐겁게 만들면 그 경보를 잃는다.
 *
 * ⚠️ 핸들러가 `null` 을 주면 「내 것이 아니다」라는 뜻이고 호출은 평소대로 서버로 간다.
 * 등록이 없으면(기본) 모든 질문이 그대로 나간다 — 실화면 동작 무변.
 */
import type {
  AdListRequest,
  AdListResponse,
  AdStatsRequest,
  AdDetailRequest,
  AdStatsResponse,
  AdThumbsRequest,
  AdFacetsResponse,
  AdThumbsResponse,
  AdDetailResponse,
  AdPublisherRequest,
  AdBrandFacesRequest,
  AdPublisherResponse,
  AdBrandFacesResponse,
} from 'src/types/ads-meta/ads-meta-ops';

import type { SimilarAdsQuery, SimilarAdsResponse } from './meta-ads-similar';
import type {
  KeywordCollectEvent,
  KeywordCollectRequest,
  KeywordCollectionResponse,
} from './meta-ads-live';

export type MetaAdsMockHandlers = {
  /** 목록 (`ads.list`) */
  adsList?: (params: AdListRequest) => AdListResponse | null;
  /** 패싯·counts·최신 수집일 (`ads.facets`) — 인자가 없다 */
  adsFacets?: () => AdFacetsResponse | null;
  /** 단건 (`ads.detail`) */
  adsDetail?: (params: AdDetailRequest) => AdDetailResponse | null;
  /** 이미지 기반 유사 소재 — `adId` 갈래와 `imageUrl` 갈래가 함께 온다 */
  similarAds?: (params: SimilarAdsQuery) => SimilarAdsResponse | null;
  /**
   * 키워드 실시간 수집의 **완료 이벤트**.
   * ⚠️ 잡히면 SSE 를 열지 않는다 — 진행 문구 없이 이 이벤트로 끝난다(약속이면 풀릴 때까지 「수집 중」이다).
   */
  keywordCollect?: (params: KeywordCollectRequest) => KeywordCollectEvent | Promise<KeywordCollectEvent> | null;
  /** 수집 1건 상세(소재 배열) */
  keywordCollection?: (idOrKeyword: string) => KeywordCollectionResponse | null;
  /** 같은 조건의 몇 축 집계 (`ads.stats`) — 허브의 「광고로 무엇을 말하나」 */
  adsStats?: (params: AdStatsRequest) => AdStatsResponse | null;
  /** 브랜드 얼굴 후보 (`ads.brandFaces`) */
  adBrandFaces?: (params: AdBrandFacesRequest) => AdBrandFacesResponse | null;
  /** 이 계정이 게시자인 광고 (`ads.byPublisher`) — 크리에이터 허브 */
  adsByPublisher?: (params: AdPublisherRequest) => AdPublisherResponse | null;
  /** 담아 둔 소재의 얼굴 (`ads.thumbs`) — 북마크 보드 카드 */
  adThumbs?: (params: AdThumbsRequest) => AdThumbsResponse | null;
};

let handlers: MetaAdsMockHandlers | null = null;

/** mockup 라우트만 부른다. `null` 이면 해제다 */
export function registerMetaAdsMock(next: MetaAdsMockHandlers | null): void {
  handlers = next;
}

export function resolveMockAdsList(params: AdListRequest): AdListResponse | null {
  return handlers?.adsList?.(params) ?? null;
}

export function resolveMockAdsFacets(): AdFacetsResponse | null {
  return handlers?.adsFacets?.() ?? null;
}

export function resolveMockAdsDetail(params: AdDetailRequest): AdDetailResponse | null {
  return handlers?.adsDetail?.(params) ?? null;
}

export function resolveMockSimilarAds(params: SimilarAdsQuery): SimilarAdsResponse | null {
  return handlers?.similarAds?.(params) ?? null;
}

export function resolveMockKeywordCollect(
  params: KeywordCollectRequest,
): KeywordCollectEvent | Promise<KeywordCollectEvent> | null {
  return handlers?.keywordCollect?.(params) ?? null;
}

export function resolveMockKeywordCollection(idOrKeyword: string): KeywordCollectionResponse | null {
  return handlers?.keywordCollection?.(idOrKeyword) ?? null;
}

export function resolveMockAdsStats(params: AdStatsRequest): AdStatsResponse | null {
  return handlers?.adsStats?.(params) ?? null;
}

export function resolveMockAdBrandFaces(params: AdBrandFacesRequest): AdBrandFacesResponse | null {
  return handlers?.adBrandFaces?.(params) ?? null;
}

export function resolveMockAdsByPublisher(params: AdPublisherRequest): AdPublisherResponse | null {
  return handlers?.adsByPublisher?.(params) ?? null;
}

export function resolveMockAdThumbs(params: AdThumbsRequest): AdThumbsResponse | null {
  return handlers?.adThumbs?.(params) ?? null;
}
